<script setup>
/**
 * "ADD LABELS" (task review item 11): the labels a handover puts on the chat, beside
 * `needs-a-person`. Used by a task's Hand off step and by Personality's "Hand chats to".
 *
 * Picked from the ACCOUNT's own Chatwoot labels, never typed: Chatwoot stores a label nobody
 * made as a tag its dashboard does not show, and the BFF refuses one on a task's save
 * (`unknown_label`). The same toggle chips as "Never answer customers with these labels".
 *
 * A label already chosen that the account no longer has stays on screen, marked, so the owner
 * can take it off; nothing is dropped behind her back. At most five, the sidecar's cap.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../i18n.js';
import {
  MAX_HANDOVER_LABELS,
  isHandoverLabel,
  toggleLabel,
} from '../helpers/hand-to.js';

const props = defineProps({
  /** The chosen labels. */
  modelValue: { type: Array, default: () => [] },
  /** The account's label titles (Chatwoot's `labels/getLabels`). */
  options: { type: Array, default: () => [] },
  hint: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const key = name => `WASLO.HAND_LABELS.${name}`;

// `needs-a-person` is always added; offering it as a choice would be a switch that does nothing.
const offered = computed(() =>
  props.options.filter(
    title => isHandoverLabel(title) && title !== 'needs-a-person'
  )
);
const gone = computed(() =>
  props.modelValue.filter(label => !props.options.includes(label))
);
const full = computed(() => props.modelValue.length >= MAX_HANDOVER_LABELS);

const toggle = title =>
  emit('update:modelValue', toggleLabel(props.modelValue, title));
</script>

<template>
  <div class="flex flex-col gap-2" data-waslo-hand-labels>
    <h3 class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t(key('HEADING')) }}
    </h3>
    <p class="mb-0 text-sm text-n-slate-11">
      {{ hint || t(key('DESCRIPTION')) }}
    </p>
    <div v-if="offered.length || gone.length" class="flex flex-wrap gap-2">
      <button
        v-for="title in offered"
        :key="title"
        type="button"
        class="inline-flex items-center h-7 px-2 text-xs font-medium rounded-lg disabled:opacity-50"
        :class="
          modelValue.includes(title)
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
        :aria-pressed="modelValue.includes(title)"
        :disabled="full && !modelValue.includes(title)"
        :data-waslo-hand-label="title"
        @click="toggle(title)"
      >
        <bdi>{{ title }}</bdi>
      </button>
      <button
        v-for="title in gone"
        :key="`gone-${title}`"
        type="button"
        class="inline-flex items-center h-7 px-2 text-xs font-medium rounded-lg bg-n-amber-3 text-n-amber-11"
        aria-pressed="true"
        :title="t(key('GONE'))"
        :data-waslo-hand-label-gone="title"
        @click="toggle(title)"
      >
        <bdi>{{ title }}</bdi>
      </button>
    </div>
    <p v-else class="mb-0 text-sm text-n-slate-10" data-waslo-hand-labels-none>
      {{ t(key('NONE')) }}
    </p>
    <p
      v-if="gone.length"
      class="mb-0 text-sm text-n-amber-11"
      data-waslo-hand-labels-gone-note
    >
      {{ t(key('GONE')) }}
    </p>
    <p
      v-else-if="full"
      class="mb-0 text-sm text-n-slate-10"
      data-waslo-hand-labels-full
    >
      {{ t(key('FULL'), { max: MAX_HANDOVER_LABELS }) }}
    </p>
  </div>
</template>
